(function(){
  /**
   * Query single element within a root
   * @param {ParentNode|Document} root
   * @param {string} sel
   * @returns {Element|null}
   */
  const qs = (root, sel) => root.querySelector(sel);

  /**
   * Query multiple elements within a root as array
   * @param {ParentNode|Document} root
   * @param {string} sel
   * @returns {Element[]}
   */
  const qsa = (root, sel) => Array.prototype.slice.call(root.querySelectorAll(sel));

  /**
   * Parse external product payload from a selector button
   * @param {Element} el
   * @returns {any|null}
   */
  const parsePayload = (el) => {
    try {
      const raw = el.getAttribute('data-external-product');
      if(!raw) return null;
      const txt = raw.replace(/&quot;/g, '"');
      return JSON.parse(txt);
    } catch(e){
      console.error('Failed to parse external product payload', e);
      return null;
    }
  };

  /**
   * Update FLAVOR label text to the selected product title (uppercased)
   * @param {Element} root
   * @param {string} title
   */
  const updateFlavorName = (root, title) => {
    try {
      const el = qs(root, '[data-flavor-name]');
      if(el){ el.textContent = (title || '').toUpperCase(); }
    } catch(e){ /* no-op */ }
  };

  /**
   * Ensure the image title element's <img> is visible
   * @param {Element|Document} root
   */
  const ensureImageTitleVisible = (root) => {
    try{
      const img = qs(root || document, '[data-variant-image-title] img, .product-showcase__image-title img');
      if(img){ img.style.display = ''; }
    } catch(e){ /* no-op */ }
  };

  /**
   * Observes the image title container to prevent it from being hidden.
   * @param {Element|Document} root
   */
  const installImageTitleVisibilityGuard = (root) => {
    try{
      let section = root && root.closest ? root.closest('.product-showcase') : root;
      if(!section) section = document;
      const holder = qs(section, '[data-variant-image-title], .product-showcase__image-title');
      if(!holder || holder.dataset.titleGuardInstalled) return;
      holder.dataset.titleGuardInstalled = '1';
      const ensure = () => { ensureImageTitleVisible(section); };
      const obs = new MutationObserver(() => { ensure(); });
      obs.observe(holder, { attributes: true, childList: true, subtree: true, attributeFilter: ['style', 'class'] });
      ensure();
    } catch(e){ /* no-op */ }
  };

  /**
   * Fetches the server-rendered selector HTML for a section and replaces its container
   * @param {Element} root
   * @param {string} productUrl
   * @param {Function} onDone
   */
  const fetchAndReplaceSelector = (root, productUrl, onDone) => {
    try{
      const sectionEl = root.closest('.product-showcase');
      if(!sectionEl) return;
      const sectionId = sectionEl.id && sectionEl.id.indexOf('section--') === 0 ? sectionEl.id.replace('section--','') : '';
      if(!sectionId || !productUrl){ if(typeof onDone === 'function') onDone(); return; }
      const url = productUrl + (productUrl.indexOf('?')>-1 ? '&' : '?') + 'sections=' + encodeURIComponent(sectionId);
      fetch(url, { credentials: 'same-origin' })
        .then(function(r){ return r.json(); })
        .then(function(json){
          const html = json && json[sectionId];
          if(!html) return;
          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          const newContainer = tmp.querySelector('.product-showcase__external .product-showcase__variants-container');
          const container = qs(root, '.product-showcase__external .product-showcase__variants-container');
          if(newContainer && container){
            container.innerHTML = newContainer.innerHTML;
          }
        })
        .catch(function(){ /* no-op */ })
        .then(function(){ if(typeof onDone === 'function') onDone(); });
    } catch(e){ if(typeof onDone === 'function') onDone(); }
  };

  /**
   * Escape JSON for HTML attribute
   * @param {any} obj
   * @returns {string}
   */
  const escapeAttrJson = (obj) => { try { return JSON.stringify(obj).replace(/"/g, '&quot;'); } catch(e){ return ''; } };

  /**
   * Build HTML for selector buttons
   * @param {Array<any>} refs
   * @param {string} activeUrl
   * @returns {string}
   */
  const buildSelectorButtons = (refs, activeUrl) => {
    if(!Array.isArray(refs)) return '';
    return refs.map(function(r){
      const bg = r.bgColor || '';
      const thumb = (r.images && r.images.thumbs && r.images.thumbs[0]) ? r.images.thumbs[0] : '';
      const payload = escapeAttrJson(r);
      const style = 'display:inline-block;margin-right:8px;border-radius:9999px;width:64px;height:64px;overflow:hidden;cursor:pointer;' + (bg ? ('background-color: ' + bg + ';') : '');
      const inner = thumb
        ? '<img src="' + thumb + '" alt="' + (r.title || '') + '" style="width:100%;height:100%;object-fit:cover;" width="64" height="64">'
        : '<span style="display:inline-block;width:100%;height:100%;border-radius:9999px;' + (bg ? ('background:' + bg + ';') : 'background:#eee;') + '"></span>';
      const activeClass = (activeUrl && r.url === activeUrl) ? ' is-active' : '';
      return '<button type="button" class="product-showcase__external-item' + activeClass + '" data-external-product="' + payload + '" style="' + style + '">' + inner + '</button>';
    }).join('');
  };

  /**
   * Unique by url string
   * @param {Array<any>} list
   */
  const uniqByUrl = (list) => {
    const seen = Object.create(null);
    const out = [];
    (list || []).forEach(function(item){
      const key = item && item.url ? item.url : JSON.stringify(item);
      if(!seen[key]){ seen[key] = true; out.push(item); }
    });
    return out;
  };

  /**
   * Replace HTML of the selector container with built buttons
   */
  const updateProductSelector = (root, refs, current) => {
    const container = qs(root, '.product-showcase__external .product-showcase__variants-container');
    if(!container) return;
    // Normalize refs to array if a single object is provided
    if(refs && !Array.isArray(refs)) refs = [refs];
    let list = [];
    if(current){ list.push(current); }
    if(Array.isArray(refs)) list = list.concat(refs);
    list = uniqByUrl(list);
    container.innerHTML = buildSelectorButtons(list, current && current.url);
  };

  /**
   * Update nutrition modal trigger and image src
   */
  const updateNutrition = (root, nutritionUrl) => {
    const img = qs(document, '.nutrition-modal__img');
    if(img){ img.src = nutritionUrl || img.src; }
    const btn = qs(root, '[data-nutrition-modal]');
    if(btn){ btn.style.display = nutritionUrl ? '' : 'none'; }
  };

  /**
   * Update add-to-cart form inputs and button state/label
   */
  const updateAddToCart = (root, payload) => {
    const form = qs(root, 'form#product-form-' + root.id.replace('section--','')) || qs(root, 'form');
    const idInput = form ? form.querySelector('input[name="id"]') : null;
    if(idInput && payload.variantId){ idInput.value = String(payload.variantId); }
    const btn = qs(root, '[data-add-to-cart]');
    if(btn){
      if(payload.title){
        btn.textContent = 'ADD TO CART - ' + payload.title;
      }
      if(payload.available === false){
        btn.classList.add('disabled','is-out-of-stock');
        btn.setAttribute('disabled','true');
        btn.setAttribute('aria-disabled','true');
        btn.style.backgroundColor = '#E0E0E0';
        btn.style.borderColor = '#D5D5D5';
        btn.style.color = '#666';
        btn.style.cursor = 'not-allowed';
      } else {
        btn.classList.remove('disabled','is-out-of-stock');
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
        btn.style.backgroundColor = '';
        btn.style.borderColor = '';
        btn.style.color = '';
        btn.style.cursor = '';
      }
    }
  };

  /**
   * Update ingredients list markup
   */
  const updateIngredients = (root, ingredients) => {
    const txt = qs(root, '.product-showcase__ingredients-text');
    if(!txt) return;
    if(Array.isArray(ingredients) && ingredients.length){
      const html = '<ul>' + ingredients.map(function(i){ return '<li>' + i + '</li>'; }).join('') + '</ul>';
      setHTML(txt, html);
    } else {
      // leave as-is if no ingredients provided
    }
  };

  /** Set textContent safely */
  const setText = (el, text) => { if(el) el.textContent = text || ''; };
  /** Set innerHTML safely */
  const setHTML = (el, html) => { if(el) el.innerHTML = html || ''; };

  /** Update Find Near You button text and href */
  const updateFindNearYou = (root, payload) => {
    if (!root || !payload) return;
    const btn = qs(root, '[data-find-near-you-button]');
    if (!btn) return;

    const text = payload.findNearYouText || btn.textContent || '';
    const href = payload.findNearYouUrl || btn.getAttribute('href') || '#';

    setText(btn, text);
    btn.setAttribute('href', href);
  };

  /** Build thumbnails slides markup */
  const buildThumbSlides = (urls) => {
    return (urls || []).map(function(url){
      return (
        '<div class="swiper-slide product-showcase__thumbnail">'
        + '<div class="product-showcase__thumbnail-img">'
        + '<img src="' + url + '" alt="">'
        + '</div>'
        + '</div>'
      );
    }).join('');
  };

  /** Build main slides markup */
  const buildMainSlides = (urls) => {
    return (urls || []).map(function(url, idx){
      return (
        '<div class="swiper-slide product-showcase__main-img">'
        + '<img src="' + url + '" alt="' + (idx+1) + '" width="auto" height="auto">'
        + '</div>'
      );
    }).join('');
  };

  /** Update the image title container with the given src or fallback */
  const updateImageTitle = (container, imageTitleSrc) => {
    if(!container) return;
    const img = container.querySelector('img');
    const fallback = container.getAttribute('data-default-image');
    const src = imageTitleSrc || fallback || '';
    if(img){ img.src = src; }
    else if(src){ container.innerHTML = '<img src="' + src + '" alt="">'; }
  };

  /** Update price and compare-at labels */
  const updatePrice = (root, priceStr, compareStr) => {
    const priceEl = qs(root, '[data-product-price]');
    const compareEl = qs(root, '.product-showcase__price-compare');
    if(priceEl) priceEl.textContent = priceStr || '';
    if(compareEl){
      if(compareStr){
        compareEl.textContent = compareStr;
        compareEl.style.display = '';
      } else {
        compareEl.textContent = '';
        compareEl.style.display = 'none';
      }
    }
  };

  /** Update gallery slides and refresh Swiper instances */
  const updateGallery = (root, images) => {
    const thumbsWrap = qs(root, '.product-showcase__thumbnails .swiper-wrapper');
    const mainWrap = qs(root, '.product-showcase__main-slider .swiper-wrapper');
    if(thumbsWrap){ setHTML(thumbsWrap, buildThumbSlides(images && images.thumbs)); }
    if(mainWrap){ setHTML(mainWrap, buildMainSlides(images && images.main)); }
    
    // Reinitialize Swiper instances with same config as initial load
    try {
      if(typeof window.initProductShowcaseSwipers === 'function'){
        window.initProductShowcaseSwipers();
      } else {
        // Fallback: try to update existing instances
        if(window.Swiper){
          qsa(root, '.swiper').forEach(function(el){ if(el.swiper && typeof el.swiper.update === 'function'){ el.swiper.update(); } });
        }
        // Re-bind thumbnail clicks as fallback
        bindThumbnailClicks(root);
      }
    } catch(e) { 
      console.warn('Error reinitializing Swiper:', e);
    }
  };

  /** Get the main Swiper instance if present */
  const getMainSwiper = (root) => {
    try {
      const el = qs(root, '.product-showcase__main-slider .swiper');
      return el && el.swiper ? el.swiper : null;
    } catch(e){ return null; }
  };

  /** Set active class on the selected thumbnail */
  const setActiveThumb = (root, index) => {
    const thumbs = qsa(root, '.product-showcase__thumbnails .swiper-slide');
    thumbs.forEach((t, i) => {
      if(i === index){ t.classList.add('swiper-slide-thumb-active'); }
      else { t.classList.remove('swiper-slide-thumb-active'); }
    });
  };

  /** Bind thumbnail clicks to update the main image/slide and active class */
  const bindThumbnailClicks = (root) => {
    const thumbs = qsa(root, '.product-showcase__thumbnails .swiper-slide');
    const mainSwiper = getMainSwiper(root);
    // Initialize active state
    const initialIndex = (mainSwiper && typeof mainSwiper.activeIndex === 'number') ? mainSwiper.activeIndex : 0;
    setActiveThumb(root, Math.min(initialIndex, Math.max(thumbs.length - 1, 0)));
    // Sync active on slide change
    if(mainSwiper && !mainSwiper._hg_thumbSyncAttached && typeof mainSwiper.on === 'function'){
      mainSwiper.on('slideChange', function(){ setActiveThumb(root, mainSwiper.activeIndex || 0); });
      mainSwiper._hg_thumbSyncAttached = true;
    }
    // Click handlers
    thumbs.forEach((el, idx) => {
      el.addEventListener('click', function(){
        setActiveThumb(root, idx);
        if(mainSwiper && typeof mainSwiper.slideTo === 'function'){
          mainSwiper.slideTo(idx);
        } else {
          const slides = qsa(root, '.product-showcase__main-img');
          slides.forEach((s, i) => { s.style.display = (i === idx) ? '' : 'none'; });
        }
      });
    });
  };

  /**
   * Handles selecting an external product/variant button
   * @param {Element} btn
   */
  const onSelect = (btn) => {
    const payload = parsePayload(btn);
    if(!payload) return;
    const section = btn.closest('.product-showcase');
    if(!section) return;

    // Title
    const titleEl = qs(section, '.product-showcase__title');
    setText(titleEl, payload.title);

    // Flavor label
    updateFlavorName(section, payload.title);

    // Description (both blocks if present)
    setHTML(qs(section, '.product-showcase__description-desktop'), payload.description);
    setHTML(qs(section, '.product-showcase__description-mobile'), payload.description);

    // Image Title (label icon metafield)
    const imageTitleEl = qs(section, '[data-variant-image-title]');
    updateImageTitle(imageTitleEl, payload.imageTitleSrc);
    // Guard against display:none applied by other scripts during subsequent interactions
    installImageTitleVisibilityGuard(section);

    // Gallery
    updateGallery(section, payload.images);

    // Price
    updatePrice(section, payload.price, payload.compareAt);

    // Ingredients
    updateIngredients(section, payload.ingredients);

    // Add to Cart (variant id and label)
    updateAddToCart(section, payload);

    // Find Near You button (text + link)
    updateFindNearYou(section, payload);

    // Nutrition image/button
    updateNutrition(section, payload.nutrition);

    // Rebuild product selector based on selected product's metafield, keeping current as active
    let ext = payload.externalRefs;
    if(ext && !Array.isArray(ext)) ext = [ext];
    updateProductSelector(section, ext || [], payload);

    // Update URL handle without reload
    try {
      if(payload.url && window.history && typeof window.history.replaceState === 'function'){
        window.history.replaceState(null, '', payload.url);
      }
    } catch(e) { /* no-op */ }

    // Fetch server-rendered selector for the selected product to guarantee metafields
    // Hide selector items BEFORE fetch to keep prior timing
    const wrap = qs(section, '.product-showcase__external .product-showcase__variants-container');
    if(wrap){ qsa(wrap, '.product-showcase__external-item').forEach(function(el){ el.style.display = 'none'; }); }
    fetchAndReplaceSelector(section, payload.url, function(){
      // Show selector items again after replacement
      const wrapAfter = qs(section, '.product-showcase__external .product-showcase__variants-container');
      if(wrapAfter){ qsa(wrapAfter, '.product-showcase__external-item').forEach(function(el){ el.style.display = ''; }); }
      // Ensure the image-title img is not hidden
      ensureImageTitleVisible(section);
      installImageTitleVisibilityGuard(section);
      // Ensure title persists if payload had no title or got cleared
      const t = qs(section, '.product-showcase__title');
      const hasPayloadTitle = !!(payload && payload.title);
      const isEmpty = t && (!t.textContent || !t.textContent.trim());
      if(t && (!hasPayloadTitle || isEmpty)){
        const restore = prevTitle || section.dataset.prevTitle || '';
        setText(t, restore);
        try { t.setAttribute('data-text', restore); } catch(e){}
      }
    });

    // Emit event for any custom hooks
    const ev = new CustomEvent('externalProductSelected', { detail: { payload: payload, section: section }});
    document.dispatchEvent(ev);
  };

  const init = () => {
    // Install guards for all sections on load
    qsa(document, '.product-showcase').forEach(function(sec){ installImageTitleVisibilityGuard(sec); });
    document.addEventListener('click', function(e){
      const target = e.target.closest('.product-showcase__external-item[data-external-product]');
      if(!target) return;
      e.preventDefault();
      onSelect(target);
    });
    // Defensive: any click inside variant containers should not hide the image-title img
    document.addEventListener('click', function(e){
      const within = e.target.closest('.product-showcase__variants-container, .product-showcase__variants-container--image');
      if(within){
        const section = within.closest('.product-showcase');
        if(section) ensureImageTitleVisible(section);
      }
    }, true);

    // Also handle variant changes (radio/select) that don't trigger onSelect
    function handleVariantInteraction(e){
      const within = e.target.closest('.product-showcase__variants-container, .product-showcase__variants-container--image');
      if(!within) return;
      const section = within.closest('.product-showcase');
      if(!section) return;
      // Re-assert immediately and after microtasks/short delay to override late mutations
      ensureImageTitleVisible(section);
      setTimeout(function(){ ensureImageTitleVisible(section); }, 0);
      setTimeout(function(){ ensureImageTitleVisible(section); }, 60);
    }
    document.addEventListener('change', handleVariantInteraction, true);
    document.addEventListener('input', handleVariantInteraction, true);
  };

  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
